import React from 'react';
import { Drawer, Space } from 'antd';
import { DollarOutlined, FileTextOutlined } from '@ant-design/icons';
import { formatApplicationPageTitle } from '../../components/applications/ApplicationDetails';
import { useGetApplicationByIdQuery } from '../../store/api/applicationsApi';
import { StatusCell } from '../../ui';
import { FinanceRouteFinanceContent, type ExtendedRoute } from './FinanceRouteModal';
import FinanceApplicationFinanceContent from './FinanceApplicationFinanceContent';

export type FinanceDetailsDrawerMode = 'route' | 'application';

export interface FinanceDetailsDrawerProps {
  open: boolean;
  mode: FinanceDetailsDrawerMode | null;
  route: ExtendedRoute | null;
  applicationId: number | null;
  /** Прокрутка/подсветка строки обязательства в «Получить от клиента» */
  focusObligationId?: number | null;
  onClose: () => void;
  onRefetchRoutes?: () => void;
  onRefetchApplications?: () => void | Promise<unknown>;
}

const FinanceDetailsDrawer: React.FC<FinanceDetailsDrawerProps> = ({
  open,
  mode,
  route,
  applicationId,
  focusObligationId = null,
  onClose,
  onRefetchRoutes,
  onRefetchApplications,
}) => {
  const { data: appForTitle } = useGetApplicationByIdQuery(applicationId!, {
    skip: !open || mode !== 'application' || applicationId == null || applicationId < 1,
  });

  const title = (() => {
    if (mode === 'route' && route) {
      return (
        <Space>
          <DollarOutlined />
          <span>Маршрут #{route.id}</span>
          {route.aggregatedStatus ? <StatusCell status={route.aggregatedStatus.overallStatus} /> : null}
        </Space>
      );
    }
    if (mode === 'application' && applicationId != null) {
      return (
        <Space>
          <FileTextOutlined />
          <span>
            {appForTitle ? formatApplicationPageTitle(appForTitle) : `Заявка #${applicationId}`}
          </span>
        </Space>
      );
    }
    return null;
  })();

  return (
    <Drawer
      title={title}
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose={false}
      styles={{
        body: {
          paddingTop: 8,
          maxHeight: 'calc(100vh - 56px)',
          overflowY: 'auto',
        },
      }}
    >
      {mode === 'route' && route ? (
        <FinanceRouteFinanceContent
          visible={open}
          route={route}
          embedded
          onRefetch={onRefetchRoutes}
        />
      ) : null}
      {mode === 'application' && applicationId != null && applicationId >= 1 ? (
        <FinanceApplicationFinanceContent
          visible={open}
          applicationId={applicationId}
          focusObligationId={focusObligationId}
          onRefetch={onRefetchApplications}
        />
      ) : null}
    </Drawer>
  );
};

export default FinanceDetailsDrawer;
